/**
 * Capture — the heart of the product. Mobile-first; mic button is the
 * primary affordance, typed fallback is right there too.
 *
 * UI sprint step 4 reskin (2026-05-11): rebuilt against the pwa-v2 design
 * vocabulary — `.panel` containers, `.v2-pill` chips, `.btn` family, mono
 * eyebrows + serif page title. No content/behavioral changes; this is a
 * pure visual pass over the existing capture state machine.
 *
 * Flow phases:
 *   input → recording → extracting → review → saving → saved
 *                                         ↘ error
 *
 * Behavior preserved verbatim:
 *   - IndexedDB capture-draft persistence + recovery banner (capture-flow §6.4)
 *   - Voice via browser SpeechRecognition (capture-flow §2.1)
 *   - Preflight A.1 soft-warning before + Create commits a duplicate
 *   - Server-side hallucinated-id filter in unmatched_mentions
 *   - KV+D1 dual idempotency via stable client_req_id
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  type EntityListItem,
  type Extraction,
  extractCapture,
  preflightEntity,
  saveCapture,
} from '../api.ts';
import {
  type CaptureDraft,
  deleteDraft,
  listDraftsForTenant,
  saveDraft,
} from '../idb/captureDrafts.ts';
import { Icons } from '../components/icons';
import { BottomSheet } from '../components/BottomSheet';
import { EntityPicker, clearEntityPickerCache } from '../components/EntityPicker';

// Suggested vs pre-confirmed threshold (capture-flow §4.3).
const PRE_CONFIRMED_THRESHOLD = 0.85;

/**
 * Chip identity used by the action sheet + swap flow. Carries enough
 * state to know how to render and what to do on Confirm/Swap/Remove.
 */
interface ChipRef {
  kind: 'account' | 'contact';
  /** What identifies this chip across taps. For extracted/linked chips
   * it's the entity_id. For creates it's the tmp_id. */
  key: string;
  /** Display label. */
  name: string;
  /** undefined for creates (no entity yet). */
  entityId?: string;
  /** undefined for creates and linked-via-preflight. */
  confidence?: number;
  /** Where the chip came from. */
  source: 'extracted' | 'linked' | 'created';
}

type Phase = 'input' | 'recording' | 'extracting' | 'review' | 'saving' | 'saved' | 'error';

interface CaptureProps {
  tenantId: string;
  onSaved?: (entityId: string | null) => void;
}

interface PendingCreate {
  tmp_id: string;
  name: string;
  type: 'account' | 'contact';
}

export function Capture(props: CaptureProps) {
  const [phase, setPhase] = useState<Phase>('input');
  const [transcript, setTranscript] = useState('');
  const [extraction, setExtraction] = useState<Extraction | null>(null);
  const [observationText, setObservationText] = useState('');
  const [creates, setCreates] = useState<PendingCreate[]>([]);
  const [linkedAccountId, setLinkedAccountId] = useState<string | null>(null);
  const [linkedContactIds, setLinkedContactIds] = useState<string[]>([]);
  // entity_id → canonical name, populated whenever we link via the
  // picker or preflight match. Used to label linked chips on the
  // review screen so the user sees the real name rather than a
  // placeholder.
  const [linkedNames, setLinkedNames] = useState<Map<string, string>>(new Map());
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  // §4.5: chips have explicit confirm/remove states. Pre-confirmed
  // chips (≥0.85) are included unless removed. Suggested chips (<0.85)
  // are NOT included unless confirmed. Both can be swapped to a
  // different entity (Swap action) — that flows into linkedAccountId /
  // linkedContactIds, replacing the extracted entity_id at save time.
  const [confirmedChips, setConfirmedChips] = useState<Set<string>>(new Set());
  const [removedChips, setRemovedChips] = useState<Set<string>>(new Set());
  // Chip-edit sheet state. When set, the bottom sheet renders the
  // Confirm/Swap/Remove options for the targeted chip.
  const [sheetChip, setSheetChip] = useState<ChipRef | null>(null);
  // Swap/add picker state. open=true mounts the EntityPicker inside a
  // BottomSheet. swapFor === undefined means "add fresh" (no chip being
  // replaced); swapFor === chipKey means "replace this chip's link."
  const [pickerState, setPickerState] = useState<
    | { open: true; kind: 'account' | 'contact'; swapFor: ChipRef | null }
    | null
  >(null);
  // Undo toast — visible for 3-5s after a destructive action.
  const [undoToast, setUndoToast] = useState<{
    text: string;
    onUndo: () => void;
  } | null>(null);
  // Inline conflict resolution per capture-flow §3.4 A.1 soft-warning.
  // Set when preflight finds a >= 0.85 lex match for a name the user is
  // trying to add. Rendered as an inline banner with explicit "Use
  // existing" / "Create new" buttons — no native confirm() dialog.
  const [pendingConflict, setPendingConflict] = useState<{
    name: string;
    type: 'account' | 'contact';
    existing: { entity_id: string; name: string; entity_type: string };
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState<string | null>(null);
  const captureStartedAtRef = useRef<string | null>(null);
  const captureMethodRef = useRef<'voice_browser' | 'typed'>('typed');
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const clientReqIdRef = useRef<string | null>(null);
  const [recoveredDraft, setRecoveredDraft] = useState<CaptureDraft | null>(null);
  const draftSaveTimerRef = useRef<number | null>(null);

  const speechSupported = useMemo(() => Boolean(getSpeechRecognitionCtor()), []);

  // Mount-time recovery scan (capture-flow §6.4).
  useEffect(() => {
    let cancelled = false;
    listDraftsForTenant(props.tenantId)
      .then((drafts) => {
        if (cancelled) return;
        const top = drafts[0];
        if (!top) return;
        if (!top.observation_text.trim() && !top.scope) return;
        setRecoveredDraft(top);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [props.tenantId]);

  // Debounced IDB persistence (400ms) of in-progress drafts during review.
  useEffect(() => {
    if (phase !== 'review') return;
    if (!extraction || !clientReqIdRef.current) return;
    if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = window.setTimeout(() => {
      const now = new Date().toISOString();
      const draft: CaptureDraft = {
        client_req_id: clientReqIdRef.current!,
        capture_method: captureMethodRef.current,
        scope: null,
        chips: {
          account_id: extraction.account?.entity_id ?? linkedAccountId ?? null,
          contact_ids: [
            ...extraction.contacts.map((c) => c.entity_id),
            ...linkedContactIds,
          ],
          creates: creates.map((c) => ({ tmp_id: c.tmp_id, name: c.name, type: c.type })),
          relationships: extraction.relationships.map((r) => ({
            type: r.type,
            from: r.from_entity_id,
            to: r.to_entity_id,
          })),
          next_action: extraction.next_action
            ? { text: extraction.next_action.text, due_date: extraction.next_action.due_date }
            : null,
        },
        observation_text: observationText,
        raw_transcript: transcript || null,
        extraction_metadata: {
          input_tokens: extraction.meta.input_tokens,
          output_tokens: extraction.meta.output_tokens,
          latency_ms: extraction.meta.latency_ms,
          candidate_count: extraction.meta.candidate_count,
        },
        pending_sync: false,
        tenant_id: props.tenantId,
        created_at: captureStartedAtRef.current ?? now,
        updated_at: now,
      };
      saveDraft(draft).catch(() => {});
    }, 400) as unknown as number;
    return () => {
      if (draftSaveTimerRef.current) window.clearTimeout(draftSaveTimerRef.current);
    };
  }, [
    phase,
    extraction,
    observationText,
    creates,
    linkedAccountId,
    linkedContactIds,
    transcript,
    props.tenantId,
  ]);

  useEffect(() => {
    if (savedToast) {
      const t = setTimeout(() => setSavedToast(null), 3500);
      return () => clearTimeout(t);
    }
  }, [savedToast]);

  // ── Voice capture via browser SpeechRecognition ────────────────────
  function startRecording() {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError('Voice capture is not supported in this browser. Type instead.');
      return;
    }
    const r = new Ctor();
    r.continuous = true;
    r.interimResults = true;
    r.lang = navigator.language.startsWith('en-') ? navigator.language : 'en-US';

    let final = '';
    let interim = '';
    r.onresult = (e) => {
      interim = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const res = e.results[i];
        if (!res) continue;
        if (res.isFinal) final += res[0].transcript;
        else interim += res[0].transcript;
      }
      setTranscript((final + ' ' + interim).trim());
    };
    r.onerror = (e) => {
      setError(`Speech error: ${e.error}`);
      setPhase('input');
    };
    r.onend = () => {
      setTranscript((final + ' ' + interim).trim());
    };

    recognitionRef.current = r;
    captureStartedAtRef.current = new Date().toISOString();
    captureMethodRef.current = 'voice_browser';
    setTranscript('');
    setError(null);
    setPhase('recording');
    r.start();
  }

  function stopRecording() {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setPhase('input');
  }

  async function submitForExtraction() {
    if (!transcript.trim()) {
      setError('Nothing to extract — type or speak first.');
      return;
    }
    if (!captureStartedAtRef.current) {
      captureStartedAtRef.current = new Date().toISOString();
      captureMethodRef.current = 'typed';
    }
    setError(null);
    setPhase('extracting');
    try {
      const result = await extractCapture({ transcript });
      clientReqIdRef.current = crypto.randomUUID();
      setExtraction(result);
      setObservationText(result.observation_text);
      setCreates([]);
      setLinkedAccountId(null);
      setLinkedContactIds([]);
      setDismissed(new Set());
      setPhase('review');
    } catch (err) {
      setError((err as Error).message);
      setPhase('error');
    }
  }

  async function handleSave() {
    if (!extraction) return;
    setError(null);
    setPhase('saving');

    const clientReqId = clientReqIdRef.current ?? crypto.randomUUID();
    const capturedAt = captureStartedAtRef.current ?? new Date().toISOString();
    const createdAccount = creates.find((c) => c.type === 'account');
    const createdContacts = creates.filter((c) => c.type === 'contact');

    // §4.3 inclusion rules:
    //   - Pre-confirmed extracted chip (conf ≥ 0.85): included unless
    //     explicitly removed OR swapped (the new linked entity takes
    //     its place).
    //   - Suggested extracted chip (conf < 0.85): included only when
    //     the user explicitly confirmed it. Otherwise dropped.
    //   - Linked (preflight match or post-swap): always included.
    //   - Created (+ Create chip): always included unless removed.
    const isExtractedIncluded = (entityId: string, confidence: number) => {
      if (removedChips.has(entityId)) return false;
      // If this chip was swapped to something else, drop the original
      // (the swap target is in linkedAccountId / linkedContactIds).
      if (linkedAccountId === entityId) return true; // no-op preflight self-link
      if (confidence >= PRE_CONFIRMED_THRESHOLD) return true;
      return confirmedChips.has(entityId);
    };

    let accountId: string | null = null;
    if (extraction.account && isExtractedIncluded(extraction.account.entity_id, extraction.account.confidence)) {
      // If user swapped or preflight-linked, prefer that. linkedAccountId
      // overrides extraction.account.entity_id when set to a different id.
      accountId =
        linkedAccountId && linkedAccountId !== extraction.account.entity_id
          ? linkedAccountId
          : extraction.account.entity_id;
    } else if (linkedAccountId) {
      // Swap-to-existing or no extracted account but a link is set.
      accountId = linkedAccountId;
    } else if (createdAccount) {
      accountId = createdAccount.tmp_id;
    }

    const includedExtractedContactIds = new Set<string>();
    for (const c of extraction.contacts) {
      if (isExtractedIncluded(c.entity_id, c.confidence)) {
        // Honor a swap: if a contact has been replaced by a linked id,
        // we don't need to do anything fancy — linkedContactIds always
        // gets merged in below.
        if (!removedChips.has(c.entity_id)) {
          includedExtractedContactIds.add(c.entity_id);
        }
      }
    }

    const contactIds = Array.from(
      new Set([
        ...includedExtractedContactIds,
        ...linkedContactIds,
        ...createdContacts.map((c) => c.tmp_id),
      ]),
    );

    try {
      const result = await saveCapture({
        client_req_id: clientReqId,
        capture_method: captureMethodRef.current,
        capture_started_at: capturedAt,
        chips: {
          account_id: accountId,
          contact_ids: contactIds,
          creates: creates.map((c) => ({ tmp_id: c.tmp_id, name: c.name, type: c.type })),
          relationships: extraction.relationships.map((r) => ({
            type: r.type,
            from: r.from_entity_id,
            to: r.to_entity_id,
          })),
          next_action: extraction.next_action
            ? { text: extraction.next_action.text, due_date: extraction.next_action.due_date }
            : null,
        },
        observation_text: observationText,
        extraction_metadata: {
          input_tokens: extraction.meta.input_tokens,
          output_tokens: extraction.meta.output_tokens,
          latency_ms: extraction.meta.latency_ms,
          candidate_count: extraction.meta.candidate_count,
        },
      });

      const target = result.primary?.entity_name ?? 'your portfolio';
      setSavedToast(result.duplicate ? `Already saved to ${target}` : `Saved to ${target}`);
      if (clientReqIdRef.current) deleteDraft(clientReqIdRef.current).catch(() => {});
      props.onSaved?.(result.primary?.entity_id ?? null);
      resetState();
    } catch (err) {
      setError((err as Error).message);
      setPhase('error');
    }
  }

  async function promoteUnmatched(name: string, type: 'account' | 'contact') {
    // Preflight A.1 soft-warning: if a near-duplicate already exists in
    // the tenant, surface it inline so the user can decide. No native
    // confirm() — capture-flow §3.4 prefers inline UI.
    try {
      const matches = await preflightEntity(name, type);
      const top = matches[0];
      if (top && top.lex_score >= 0.85) {
        setPendingConflict({
          name,
          type,
          existing: { entity_id: top.entity_id, name: top.name, entity_type: top.type },
        });
        return;
      }
    } catch {
      // Preflight failure is non-fatal — proceed with create.
    }

    // No conflict: commit the create immediately.
    commitCreate(name, type);
  }

  function commitCreate(name: string, type: 'account' | 'contact') {
    const tmpId = `tmp_${crypto.randomUUID().slice(0, 12)}`;
    setCreates((prev) => {
      if (type === 'account' && prev.find((c) => c.type === 'account')) return prev;
      if (prev.length >= 3) return prev;
      return [...prev, { tmp_id: tmpId, name, type }];
    });
    setDismissed((prev) => new Set(prev).add(`${name.toLowerCase()}|${type}`));
  }

  function resolveConflictUseExisting() {
    if (!pendingConflict) return;
    const { existing, type, name } = pendingConflict;
    setLinkedNames((prev) => {
      if (prev.get(existing.entity_id) === existing.name) return prev;
      const next = new Map(prev);
      next.set(existing.entity_id, existing.name);
      return next;
    });
    if (type === 'account') setLinkedAccountId(existing.entity_id);
    else {
      setLinkedContactIds((prev) =>
        prev.includes(existing.entity_id) ? prev : [...prev, existing.entity_id],
      );
    }
    setDismissed((prev) => new Set(prev).add(`${name.toLowerCase()}|${type}`));
    setPendingConflict(null);
  }

  function resolveConflictCreateNew() {
    if (!pendingConflict) return;
    const { name, type } = pendingConflict;
    commitCreate(name, type);
    setPendingConflict(null);
  }

  function cancelConflict() {
    setPendingConflict(null);
  }

  // ── Chip action handlers (drive the §4.5 sheet) ──────────────────────

  function openChipSheet(chip: ChipRef) {
    setSheetChip(chip);
  }

  function confirmChip(chip: ChipRef) {
    setConfirmedChips((prev) => {
      const next = new Set(prev);
      next.add(chip.key);
      return next;
    });
    setSheetChip(null);
    setUndoToast({
      text: `Confirmed ${chip.name}`,
      onUndo: () => {
        setConfirmedChips((prev) => {
          const next = new Set(prev);
          next.delete(chip.key);
          return next;
        });
        setUndoToast(null);
      },
    });
  }

  function removeChip(chip: ChipRef) {
    // For created chips, drop from creates entirely. For extracted/
    // linked chips, just mark removed so the save filter drops them.
    if (chip.source === 'created') {
      setCreates((prev) => prev.filter((c) => c.tmp_id !== chip.key));
    } else {
      setRemovedChips((prev) => {
        const next = new Set(prev);
        next.add(chip.key);
        return next;
      });
      // Also clear any linked override if this was the linked entity.
      if (chip.kind === 'account' && linkedAccountId === chip.entityId) {
        setLinkedAccountId(null);
      }
      if (chip.kind === 'contact' && chip.entityId && linkedContactIds.includes(chip.entityId)) {
        setLinkedContactIds((prev) => prev.filter((id) => id !== chip.entityId));
      }
    }
    setSheetChip(null);
    setUndoToast({
      text: `Removed ${chip.name}`,
      onUndo: () => {
        if (chip.source === 'created') {
          // Re-insert the create record.
          setCreates((prev) => {
            if (prev.find((c) => c.tmp_id === chip.key)) return prev;
            return [...prev, { tmp_id: chip.key, name: chip.name, type: chip.kind }];
          });
        } else {
          setRemovedChips((prev) => {
            const next = new Set(prev);
            next.delete(chip.key);
            return next;
          });
        }
        setUndoToast(null);
      },
    });
  }

  function openSwapPicker(chip: ChipRef) {
    setPickerState({ open: true, kind: chip.kind, swapFor: chip });
    setSheetChip(null);
  }

  function openAddPicker(kind: 'account' | 'contact') {
    setPickerState({ open: true, kind, swapFor: null });
  }

  function handlePickerExisting(entity: EntityListItem) {
    if (!pickerState) return;
    const { kind, swapFor } = pickerState;

    if (swapFor) {
      // Swap: mark the original chip as removed (it stops counting) and
      // wire the new entity into the linked set.
      if (swapFor.source !== 'created' && swapFor.entityId) {
        setRemovedChips((prev) => {
          const next = new Set(prev);
          next.add(swapFor.key);
          return next;
        });
      } else if (swapFor.source === 'created') {
        setCreates((prev) => prev.filter((c) => c.tmp_id !== swapFor.key));
      }
    }

    setLinkedNames((prev) => {
      if (prev.get(entity.id) === entity.name) return prev;
      const next = new Map(prev);
      next.set(entity.id, entity.name);
      return next;
    });
    if (kind === 'account') {
      setLinkedAccountId(entity.id);
    } else {
      setLinkedContactIds((prev) =>
        prev.includes(entity.id) ? prev : [...prev, entity.id],
      );
    }
    setPickerState(null);
  }

  function handlePickerCreate(name: string) {
    if (!pickerState) return;
    const { kind, swapFor } = pickerState;
    // If this is a swap, drop the original first.
    if (swapFor) {
      if (swapFor.source !== 'created' && swapFor.entityId) {
        setRemovedChips((prev) => {
          const next = new Set(prev);
          next.add(swapFor.key);
          return next;
        });
      } else if (swapFor.source === 'created') {
        setCreates((prev) => prev.filter((c) => c.tmp_id !== swapFor.key));
      }
    }
    setPickerState(null);
    // Use the existing promoteUnmatched path so we get the A.1 preflight
    // soft-warning before committing a duplicate.
    promoteUnmatched(name, kind);
  }

  function closePicker() {
    setPickerState(null);
  }

  // Auto-dismiss undo toast after 3.5s.
  useEffect(() => {
    if (!undoToast) return;
    const t = setTimeout(() => setUndoToast(null), 3500);
    return () => clearTimeout(t);
  }, [undoToast]);

  // Clear the entity-picker cache when leaving review so a future
  // capture sees fresh portfolio data (the user may have just created
  // new entities through this very capture).
  useEffect(() => {
    if (phase !== 'review') clearEntityPickerCache();
  }, [phase]);

  function dismissUnmatched(name: string, type: 'account' | 'contact') {
    setDismissed((prev) => new Set(prev).add(`${name.toLowerCase()}|${type}`));
  }
  function removeCreate(tmpId: string) {
    setCreates((prev) => prev.filter((c) => c.tmp_id !== tmpId));
  }
  function resetState() {
    setPhase('input');
    setTranscript('');
    setExtraction(null);
    setObservationText('');
    setCreates([]);
    setLinkedAccountId(null);
    setLinkedContactIds([]);
    setDismissed(new Set());
    setConfirmedChips(new Set());
    setRemovedChips(new Set());
    setLinkedNames(new Map());
    setSheetChip(null);
    setPickerState(null);
    setUndoToast(null);
    setPendingConflict(null);
    setRecoveredDraft(null);
    captureStartedAtRef.current = null;
    captureMethodRef.current = 'typed';
    clientReqIdRef.current = null;
  }

  function continueRecovered(draft: CaptureDraft) {
    clientReqIdRef.current = draft.client_req_id;
    captureStartedAtRef.current = draft.created_at;
    captureMethodRef.current =
      draft.capture_method === 'voice_browser' ? 'voice_browser' : 'typed';

    const reconstructed: Extraction = {
      account: draft.chips.account_id
        ? {
            entity_id: draft.chips.account_id,
            confidence: 1,
            name_mentioned: '(restored)',
            entity_name: '(restored)',
          }
        : null,
      contacts: draft.chips.contact_ids.map((id) => ({
        entity_id: id,
        confidence: 1,
        name_mentioned: '(restored)',
        entity_name: '(restored)',
      })),
      relationships: draft.chips.relationships.map((r) => ({
        type: r.type,
        from_entity_id: r.from,
        to_entity_id: r.to,
        confidence: 1,
      })),
      unmatched_mentions: [],
      next_action: draft.chips.next_action
        ? {
            text: draft.chips.next_action.text,
            due_date: draft.chips.next_action.due_date,
            confidence: 1,
          }
        : null,
      observation_text: draft.observation_text,
      meta: {
        candidate_count: 0,
        input_tokens: 0,
        output_tokens: 0,
        latency_ms: 0,
        model: 'restored-from-draft',
      },
    };
    setExtraction(reconstructed);
    setObservationText(draft.observation_text);
    setCreates(
      draft.chips.creates.map((c) => ({
        tmp_id: c.tmp_id,
        name: c.name,
        type: c.type as 'account' | 'contact',
      })),
    );
    setLinkedAccountId(null);
    setLinkedContactIds([]);
    setRecoveredDraft(null);
    setPhase('review');
  }

  function discardRecovered(draft: CaptureDraft) {
    deleteDraft(draft.client_req_id).catch(() => {});
    setRecoveredDraft(null);
  }

  // ──────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────

  return (
    <div class="v2-page">
      <div class="page-head">
        <div>
          <div class="page-eyebrow">
            {phase === 'review' ? 'Review' : 'Capture'}
          </div>
          <h1 class="v2-page-title">
            {phase === 'review' ? 'Confirm what to save' : 'What just happened?'}
          </h1>
          {phase === 'input' && (
            <div class="page-sub">
              Speak or type — Known Accounts figures out who it's about.
            </div>
          )}
        </div>
      </div>

      {error && (
        <div class="error-banner" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icons.Close size={14} />
          <span style={{ flex: 1 }}>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            class="btn btn-ghost"
            style={{ height: 24, padding: '0 8px' }}
            aria-label="Dismiss error"
          >
            Dismiss
          </button>
        </div>
      )}

      {savedToast && (
        <div
          style={{
            padding: '10px 14px',
            background: 'var(--ok-tint)',
            border: '1px solid rgba(74, 222, 128, 0.3)',
            color: 'var(--ok)',
            borderRadius: 'var(--r)',
            fontSize: 13,
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Icons.Check size={14} />
          {savedToast}
        </div>
      )}

      {recoveredDraft && phase === 'input' && (
        <RecoveryBanner
          draft={recoveredDraft}
          onContinue={() => continueRecovered(recoveredDraft)}
          onDiscard={() => discardRecovered(recoveredDraft)}
        />
      )}

      {(phase === 'input' || phase === 'recording') && (
        <InputBlock
          transcript={transcript}
          onTranscriptChange={setTranscript}
          phase={phase}
          speechSupported={speechSupported}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
          onContinue={submitForExtraction}
        />
      )}

      {phase === 'extracting' && (
        <div class="panel" style={{ padding: 32, textAlign: 'center' }}>
          <div
            style={{
              fontFamily: 'var(--f-mono)',
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--text-muted)',
              marginBottom: 12,
            }}
          >
            Understanding…
          </div>
          <div
            class="skel"
            style={{ height: 14, width: '60%', margin: '0 auto 8px', borderRadius: 4 }}
          />
          <div
            class="skel"
            style={{ height: 14, width: '40%', margin: '0 auto', borderRadius: 4 }}
          />
        </div>
      )}

      {phase === 'review' && extraction && (
        <>
          {pendingConflict && (
            <ConflictBanner
              name={pendingConflict.name}
              type={pendingConflict.type}
              existingName={pendingConflict.existing.name}
              onUseExisting={resolveConflictUseExisting}
              onCreateNew={resolveConflictCreateNew}
              onCancel={cancelConflict}
            />
          )}
          <ReviewBlock
            extraction={extraction}
            observationText={observationText}
            setObservationText={setObservationText}
            creates={creates}
            dismissed={dismissed}
            confirmedChips={confirmedChips}
            removedChips={removedChips}
            linkedAccountId={linkedAccountId}
            linkedContactIds={linkedContactIds}
            linkedNames={linkedNames}
            onPromoteUnmatched={promoteUnmatched}
            onDismissUnmatched={dismissUnmatched}
            onChipTap={openChipSheet}
            onOpenAddPicker={openAddPicker}
            onSave={handleSave}
            onDiscard={() => {
              if (clientReqIdRef.current) deleteDraft(clientReqIdRef.current).catch(() => {});
              resetState();
            }}
          />
        </>
      )}

      {/* Bottom sheet for chip edit (§4.5) */}
      <BottomSheet
        open={sheetChip !== null}
        onClose={() => setSheetChip(null)}
        eyebrow={
          sheetChip
            ? sheetChip.source === 'created'
              ? `Created ${sheetChip.kind}`
              : sheetChip.confidence !== undefined && sheetChip.confidence < PRE_CONFIRMED_THRESHOLD
                ? `Suggested ${sheetChip.kind}`
                : `Confirmed ${sheetChip.kind}`
            : undefined
        }
        title={sheetChip?.name}
      >
        {sheetChip && (
          <>
            {sheetChip.source !== 'created' &&
              sheetChip.confidence !== undefined &&
              sheetChip.confidence < PRE_CONFIRMED_THRESHOLD &&
              !confirmedChips.has(sheetChip.key) && (
                <button
                  type="button"
                  class="sheet-action sheet-action-primary"
                  onClick={() => confirmChip(sheetChip)}
                >
                  <Icons.Check size={16} className="ico" />
                  Confirm
                  <span class="sheet-action-meta">use as-is</span>
                </button>
              )}
            <button
              type="button"
              class="sheet-action"
              onClick={() => openSwapPicker(sheetChip)}
            >
              <Icons.Refresh size={16} className="ico" />
              Swap to another {sheetChip.kind}
              <span class="sheet-action-meta">pick existing</span>
            </button>
            <button
              type="button"
              class="sheet-action sheet-action-danger"
              onClick={() => removeChip(sheetChip)}
            >
              <Icons.Close size={16} className="ico" />
              Remove
              <span class="sheet-action-meta">exclude from save</span>
            </button>
          </>
        )}
      </BottomSheet>

      {/* Bottom sheet for Swap / Add picker */}
      <BottomSheet
        open={pickerState !== null}
        onClose={closePicker}
        eyebrow={
          pickerState
            ? pickerState.swapFor
              ? `Swap ${pickerState.kind}`
              : `Add ${pickerState.kind}`
            : undefined
        }
        title={
          pickerState
            ? pickerState.swapFor
              ? `Replace ${pickerState.swapFor.name}`
              : `Pick or create a${pickerState.kind === 'account' ? 'n' : ''} ${pickerState.kind}`
            : undefined
        }
      >
        {pickerState && (
          <EntityPicker
            type={pickerState.kind}
            excludeEntityId={pickerState.swapFor?.entityId}
            onPickExisting={handlePickerExisting}
            onCreateNew={handlePickerCreate}
            placeholder={`Search ${pickerState.kind}s…`}
          />
        )}
      </BottomSheet>

      {/* Undo toast */}
      {undoToast && (
        <div class="undo-toast">
          <span>{undoToast.text}</span>
          <button type="button" onClick={undoToast.onUndo}>
            Undo
          </button>
        </div>
      )}

      {phase === 'saving' && (
        <div class="panel" style={{ padding: 32, textAlign: 'center' }}>
          <div
            style={{
              fontFamily: 'var(--f-mono)',
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--text-muted)',
            }}
          >
            Saving…
          </div>
        </div>
      )}

      {phase === 'error' && (
        <div class="panel" style={{ padding: 24, textAlign: 'center' }}>
          <button type="button" class="btn" onClick={resetState}>
            Start over
          </button>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Input block — textarea + mic + continue
// ───────────────────────────────────────────────────────────────────────────

function InputBlock(props: {
  transcript: string;
  onTranscriptChange: (s: string) => void;
  phase: 'input' | 'recording';
  speechSupported: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onContinue: () => void;
}) {
  return (
    <div class="panel" style={{ padding: 18 }}>
      <textarea
        class="textarea"
        placeholder={"What happened? Who was there? What's next?\nMet with Bob at Acme, sending pricing tomorrow."}
        value={props.transcript}
        onInput={(e) =>
          props.onTranscriptChange((e.target as HTMLTextAreaElement).value)
        }
        disabled={props.phase === 'recording'}
        rows={6}
        style={{ fontSize: 15, lineHeight: 1.55, minHeight: 140 }}
      />
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 12,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {props.speechSupported && props.phase !== 'recording' && (
          <button
            type="button"
            onClick={props.onStartRecording}
            class="btn"
            style={{
              color: 'var(--danger)',
              borderColor: 'rgba(248,113,113,0.3)',
              background: 'var(--danger-tint)',
            }}
          >
            <Icons.Dot size={10} />
            Record
          </button>
        )}
        {props.phase === 'recording' && (
          <button
            type="button"
            onClick={props.onStopRecording}
            class="btn"
            style={{
              color: 'var(--danger)',
              borderColor: 'var(--danger)',
              background: 'var(--danger-tint)',
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: 'var(--danger)',
                display: 'inline-block',
                animation: 'pulse 1.2s ease-in-out infinite',
              }}
            />
            Stop
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={props.onContinue}
          disabled={!props.transcript.trim() || props.phase === 'recording'}
          class="btn btn-primary"
        >
          Continue
          <Icons.ArrowRight size={14} />
        </button>
      </div>
      {!props.speechSupported && (
        <p
          style={{
            marginTop: 8,
            fontSize: 11.5,
            color: 'var(--text-muted)',
            fontFamily: 'var(--f-mono)',
          }}
        >
          Voice capture unavailable in this browser. Typed input works the same way.
        </p>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Recovery banner
// ───────────────────────────────────────────────────────────────────────────

function RecoveryBanner(props: {
  draft: CaptureDraft;
  onContinue: () => void;
  onDiscard: () => void;
}) {
  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'var(--warn-tint)',
        border: '1px solid rgba(251, 191, 36, 0.3)',
        borderRadius: 'var(--r)',
        marginBottom: 16,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--f-mono)',
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--warn)',
          marginBottom: 6,
        }}
      >
        Recovered draft · {relativeTimeShort(props.draft.updated_at)}
      </div>
      <div style={{ fontSize: 13.5, color: 'var(--text)', marginBottom: 10 }}>
        "{truncate(props.draft.observation_text || '(no text yet)', 140)}"
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" class="btn btn-primary" onClick={props.onContinue}>
          Continue
        </button>
        <button type="button" class="btn btn-ghost" onClick={props.onDiscard}>
          Discard
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Review subview
// ───────────────────────────────────────────────────────────────────────────

function ReviewBlock(props: {
  extraction: Extraction;
  observationText: string;
  setObservationText: (s: string) => void;
  creates: PendingCreate[];
  dismissed: Set<string>;
  confirmedChips: Set<string>;
  removedChips: Set<string>;
  linkedAccountId: string | null;
  linkedContactIds: string[];
  linkedNames: Map<string, string>;
  onPromoteUnmatched: (name: string, type: 'account' | 'contact') => void;
  onDismissUnmatched: (name: string, type: 'account' | 'contact') => void;
  onChipTap: (chip: ChipRef) => void;
  onOpenAddPicker: (kind: 'account' | 'contact') => void;
  onSave: () => void;
  onDiscard: () => void;
}) {
  const {
    extraction,
    observationText,
    setObservationText,
    creates,
    dismissed,
    confirmedChips,
    removedChips,
    linkedAccountId,
    linkedContactIds,
    linkedNames,
    onPromoteUnmatched: _onPromoteUnmatched,
    onDismissUnmatched,
    onChipTap,
    onOpenAddPicker,
    onSave,
    onDiscard,
  } = props;
  void _onPromoteUnmatched; // kept on the prop signature for callers that still need it

  const createdAccount = creates.find((c) => c.type === 'account');
  const createdContacts = creates.filter((c) => c.type === 'contact');
  const visibleUnmatched = extraction.unmatched_mentions.filter(
    (m) => !dismissed.has(`${m.name.toLowerCase()}|${m.type}`),
  );

  // Compute what counts as "having an account" for save eligibility.
  // Matches the handleSave inclusion rules: pre-confirmed extracted,
  // confirmed suggested, linked (swap or preflight match), or created.
  const extractedAccountIncluded = (() => {
    if (!extraction.account) return false;
    if (removedChips.has(extraction.account.entity_id)) return false;
    if (extraction.account.confidence >= PRE_CONFIRMED_THRESHOLD) return true;
    return confirmedChips.has(extraction.account.entity_id);
  })();
  const hasAccount =
    extractedAccountIncluded || !!linkedAccountId || !!createdAccount;

  // §4.4 — "N to review" banner counts suggested extracted chips (<0.85)
  // that the user hasn't yet confirmed or removed.
  const toReviewCount = (() => {
    let n = 0;
    if (extraction.account &&
        extraction.account.confidence < PRE_CONFIRMED_THRESHOLD &&
        !confirmedChips.has(extraction.account.entity_id) &&
        !removedChips.has(extraction.account.entity_id)) {
      n += 1;
    }
    for (const c of extraction.contacts) {
      if (
        c.confidence < PRE_CONFIRMED_THRESHOLD &&
        !confirmedChips.has(c.entity_id) &&
        !removedChips.has(c.entity_id)
      ) {
        n += 1;
      }
    }
    return n;
  })();

  const accountSlotFull = hasAccount;
  const hasUnmatchedAccount = visibleUnmatched.some((m) => m.type === 'account');
  const contactSlotFull = creates.length >= 3 && !creates.find((c) => c.type === 'account');

  // Build the chip list for the Account panel — extracted (if not removed),
  // any swap-target (linkedAccountId) shown as a confirmed chip, or a
  // create chip.
  const accountChips: ChipRef[] = [];
  if (
    extraction.account &&
    !removedChips.has(extraction.account.entity_id) &&
    // If swapped, the linkedAccountId chip below represents the new entity.
    (!linkedAccountId || linkedAccountId === extraction.account.entity_id)
  ) {
    accountChips.push({
      kind: 'account',
      key: extraction.account.entity_id,
      // Prefer the canonical entity name; fall back to what the user
      // said only if the server didn't enrich (older response shape).
      name: extraction.account.entity_name ?? extraction.account.name_mentioned,
      entityId: extraction.account.entity_id,
      confidence: extraction.account.confidence,
      source: 'extracted',
    });
  }
  if (
    linkedAccountId &&
    extraction.account?.entity_id !== linkedAccountId &&
    !removedChips.has(linkedAccountId)
  ) {
    accountChips.push({
      kind: 'account',
      key: linkedAccountId,
      name: linkedNames.get(linkedAccountId) ?? 'Account',
      entityId: linkedAccountId,
      source: 'linked',
    });
  }
  if (createdAccount) {
    accountChips.push({
      kind: 'account',
      key: createdAccount.tmp_id,
      name: createdAccount.name,
      source: 'created',
    });
  }

  // Contact chips: extracted (minus removed), linked-not-already-extracted,
  // and creates.
  const contactChips: ChipRef[] = [];
  for (const c of extraction.contacts) {
    if (removedChips.has(c.entity_id)) continue;
    contactChips.push({
      kind: 'contact',
      key: c.entity_id,
      name: c.entity_name ?? c.name_mentioned,
      entityId: c.entity_id,
      confidence: c.confidence,
      source: 'extracted',
    });
  }
  for (const id of linkedContactIds) {
    if (extraction.contacts.some((c) => c.entity_id === id)) continue;
    if (removedChips.has(id)) continue;
    contactChips.push({
      kind: 'contact',
      key: id,
      name: linkedNames.get(id) ?? 'Contact',
      entityId: id,
      source: 'linked',
    });
  }
  for (const c of createdContacts) {
    contactChips.push({
      kind: 'contact',
      key: c.tmp_id,
      name: c.name,
      source: 'created',
    });
  }

  void hasUnmatchedAccount; // banner copy moved out of this block

  return (
    <div class="stack-lg">
      {toReviewCount > 0 && (
        <div
          class="review-banner"
          onClick={() => {
            // Scroll to first suggested chip and pulse — for MVP we just
            // bring it into view, no pulse yet (Phase 1.x animation).
            const node = document.querySelector('[data-chip-suggested="1"]');
            node?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }}
        >
          <Icons.Sparkles size={14} />
          <span>
            <strong>{toReviewCount} to review</strong> · saved only if confirmed
          </span>
          <span class="review-banner-arrow">↓</span>
        </div>
      )}

      <div class="panel">
        <div class="panel-head">
          <h3>
            <Icons.Building size={13} />
            Account
          </h3>
        </div>
        <div class="panel-body">
          {accountChips.length > 0 ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {accountChips.map((chip) => (
                <TappableChip
                  key={chip.key}
                  chip={chip}
                  confirmed={confirmedChips.has(chip.key)}
                  onTap={() => onChipTap(chip)}
                />
              ))}
            </div>
          ) : (
            <p
              style={{
                fontSize: 12.5,
                color: 'var(--text-muted)',
                margin: '0 0 10px',
                lineHeight: 1.5,
              }}
            >
              No account matched. Pick or create one to save this capture.
            </p>
          )}
          {accountChips.length === 0 && (
            <button
              type="button"
              class="btn"
              onClick={() => onOpenAddPicker('account')}
            >
              <Icons.Plus size={12} />
              Pick or create account
            </button>
          )}
        </div>
      </div>

      <div class="panel">
        <div class="panel-head">
          <h3>
            <Icons.Contacts size={13} />
            Contacts
            {contactChips.length > 0 && <span class="count">{contactChips.length}</span>}
          </h3>
        </div>
        <div class="panel-body">
          {contactChips.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                marginBottom: 10,
              }}
            >
              {contactChips.map((chip) => (
                <TappableChip
                  key={chip.key}
                  chip={chip}
                  confirmed={confirmedChips.has(chip.key)}
                  onTap={() => onChipTap(chip)}
                />
              ))}
            </div>
          )}
          {contactSlotFull ? (
            <p
              style={{
                fontSize: 12,
                color: 'var(--text-faint)',
                margin: 0,
                fontFamily: 'var(--f-mono)',
              }}
            >
              Max 3 new entities per capture — remove one to add another.
            </p>
          ) : (
            <button
              type="button"
              class="btn"
              onClick={() => onOpenAddPicker('contact')}
            >
              <Icons.Plus size={12} />
              Pick or create contact
            </button>
          )}
        </div>
      </div>

      {visibleUnmatched.length > 0 && (
        <div class="panel">
          <div class="panel-head">
            <h3>
              <Icons.Plus size={13} />
              Unmatched mentions
              <span class="count">{visibleUnmatched.length}</span>
            </h3>
          </div>
          <div class="panel-body">
            <p
              style={{
                fontSize: 12,
                color: 'var(--text-muted)',
                margin: '0 0 10px',
                lineHeight: 1.5,
              }}
            >
              These names didn't match anyone in your portfolio. Tap to create a new
              entity, or × to leave it as observation text only.
            </p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {visibleUnmatched.map((m, i) => {
                const blocked = m.type === 'account' && accountSlotFull;
                return (
                  <UnmatchedChip
                    key={`${m.name}-${m.type}-${i}`}
                    label={m.name}
                    type={m.type}
                    disabled={blocked}
                    onCreate={() => _onPromoteUnmatched(m.name, m.type)}
                    onDismiss={() => onDismissUnmatched(m.name, m.type)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {extraction.next_action && (
        <div class="panel">
          <div class="panel-head">
            <h3>
              <Icons.Flag size={13} />
              Next action
            </h3>
          </div>
          <div class="panel-body">
            <div
              style={{
                padding: '10px 12px',
                background: 'var(--accent-tint-08)',
                border: '1px solid var(--accent-tint-22)',
                borderRadius: 'var(--r-sm)',
                color: 'var(--text)',
                fontSize: 13.5,
                lineHeight: 1.5,
              }}
            >
              {extraction.next_action.text}
              {extraction.next_action.due_date && (
                <span class="mono" style={{ color: 'var(--accent-hi)', marginLeft: 6 }}>
                  · due {extraction.next_action.due_date}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div class="panel">
        <div class="panel-head">
          <h3>
            <Icons.FileText size={13} />
            What happened
          </h3>
        </div>
        <div class="panel-body">
          <textarea
            class="textarea"
            value={observationText}
            onInput={(e) =>
              setObservationText((e.target as HTMLTextAreaElement).value)
            }
            rows={4}
          />
        </div>
      </div>

      <div class="capture-save-bar">
        <button
          type="button"
          class="capture-save-btn"
          onClick={onSave}
          disabled={!hasAccount || !observationText.trim()}
        >
          {hasAccount ? (observationText.trim() ? 'Save' : 'Add a note to save') : 'Add an account to save'}
          {hasAccount && observationText.trim() && <Icons.ArrowRight size={16} />}
        </button>
        <button
          type="button"
          class="btn btn-ghost"
          onClick={onDiscard}
          style={{ marginTop: 8, width: '100%' }}
        >
          Discard
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Chips
// ───────────────────────────────────────────────────────────────────────────

// ───────────────────────────────────────────────────────────────────────────
// Conflict banner — inline soft-warning when preflight finds a near-dup.
// Replaces the native window.confirm() popup (capture-flow §3.4 prefers
// inline UI over native dialogs; native button labels can't be changed).
// ───────────────────────────────────────────────────────────────────────────

function ConflictBanner(props: {
  name: string;
  type: 'account' | 'contact';
  existingName: string;
  onUseExisting: () => void;
  onCreateNew: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      style={{
        padding: '14px 16px',
        background: 'var(--warn-tint)',
        border: '1px solid rgba(251, 191, 36, 0.35)',
        borderRadius: 'var(--r-lg)',
        marginBottom: 16,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--f-mono)',
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--warn)',
          marginBottom: 6,
        }}
      >
        Possible duplicate
      </div>
      <div
        style={{
          fontSize: 13.5,
          color: 'var(--text)',
          lineHeight: 1.5,
          marginBottom: 12,
        }}
      >
        You typed <strong>"{props.name}"</strong>, but a {props.type} named{' '}
        <strong>"{props.existingName}"</strong> already exists in your
        portfolio.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          class="btn btn-primary"
          onClick={props.onUseExisting}
        >
          <Icons.Check size={12} />
          Use existing "{props.existingName}"
        </button>
        <button type="button" class="btn" onClick={props.onCreateNew}>
          <Icons.Plus size={12} />
          Create new "{props.name}" anyway
        </button>
        <button type="button" class="btn btn-ghost" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Manual add form — for when Haiku didn't surface a usable name. Runs
// preflight A.1 via the same promoteUnmatched path.
// ───────────────────────────────────────────────────────────────────────────

function ManualAddForm(props: {
  type: 'account' | 'contact';
  placeholder: string;
  disabled?: boolean;
  disabledHint?: string;
  onAdd: (name: string) => void;
}) {
  const [name, setName] = useState('');

  function submit(e: Event) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || props.disabled) return;
    props.onAdd(trimmed);
    setName('');
  }

  if (props.disabled && props.disabledHint) {
    return (
      <p
        style={{
          fontSize: 12,
          color: 'var(--text-faint)',
          margin: 0,
          fontFamily: 'var(--f-mono)',
        }}
      >
        {props.disabledHint}
      </p>
    );
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex',
        gap: 6,
        alignItems: 'center',
      }}
    >
      <input
        type="text"
        class="input"
        placeholder={props.placeholder}
        value={name}
        onInput={(e) => setName((e.target as HTMLInputElement).value)}
        disabled={props.disabled}
        style={{ flex: 1, fontSize: 13, padding: '8px 12px' }}
      />
      <button
        type="submit"
        class="btn"
        disabled={!name.trim() || props.disabled}
        style={{
          color: 'var(--ok)',
          background: 'var(--ok-tint)',
          borderColor: 'rgba(74,222,128,0.2)',
        }}
      >
        <Icons.Plus size={12} />
        Add
      </button>
    </form>
  );
}

/**
 * TappableChip — single visual surface for any chip on the review page.
 * State-driven visuals per capture-flow §4.3:
 *   pre-confirmed (≥0.85 or confirmed-suggested): solid accent tint
 *   suggested (<0.85, not confirmed): dashed border + `?` suffix
 *   created (+ Create): solid green tint
 *   linked (swap target): solid accent tint
 * Tap opens the chip-action sheet (§4.5).
 */
function TappableChip(props: {
  chip: ChipRef;
  confirmed: boolean;
  onTap: () => void;
}) {
  const { chip, confirmed, onTap } = props;
  const isSuggested =
    chip.source === 'extracted' &&
    chip.confidence !== undefined &&
    chip.confidence < PRE_CONFIRMED_THRESHOLD &&
    !confirmed;

  // Pick the visual flavor
  let pillClass = 'pill-info';
  let borderStyle: 'solid' | 'dashed' = 'solid';
  if (chip.source === 'created') {
    pillClass = 'pill-ok';
    borderStyle = 'dashed';
  } else if (isSuggested) {
    pillClass = 'pill-warn';
    borderStyle = 'dashed';
  }

  return (
    <button
      type="button"
      class={`v2-pill ${pillClass}`}
      onClick={onTap}
      data-chip-suggested={isSuggested ? '1' : undefined}
      title={
        chip.confidence !== undefined
          ? `confidence ${(chip.confidence * 100).toFixed(0)}%`
          : undefined
      }
      style={{
        height: 26,
        padding: '0 12px',
        border: `1px ${borderStyle} ${
          isSuggested
            ? 'rgba(251, 191, 36, 0.45)'
            : chip.source === 'created'
              ? 'rgba(74, 222, 128, 0.4)'
              : 'transparent'
        }`,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {chip.source === 'created' && <Icons.Plus size={11} />}
      <span class="dot" />
      <span>{chip.name}</span>
      {isSuggested && (
        <span style={{ color: 'var(--warn)', fontWeight: 700, marginLeft: 4 }}>?</span>
      )}
    </button>
  );
}

function MatchedChip({ label, confidence }: { label: string; confidence: number }) {
  const high = confidence >= 0.85;
  return (
    <span
      class={`v2-pill ${high ? 'pill-ok' : 'pill-warn'}`}
      style={{
        height: 24,
        padding: '0 10px',
        borderStyle: high ? 'solid' : 'dashed',
        borderWidth: high ? 0 : 1,
        borderColor: high ? 'transparent' : 'var(--warn)',
      }}
      title={`confidence ${(confidence * 100).toFixed(0)}%`}
    >
      <span class="dot" />
      {label}
      {!high && <span style={{ marginLeft: 4, opacity: 0.7 }}>?</span>}
    </span>
  );
}

function CreateChip(props: {
  label: string;
  type: 'account' | 'contact';
  onRemove: () => void;
}) {
  return (
    <span
      class="v2-pill pill-info"
      style={{
        height: 24,
        padding: '0 6px 0 10px',
        gap: 6,
      }}
      title={`New ${props.type}`}
    >
      <Icons.Plus size={10} />
      {props.label}
      <button
        type="button"
        onClick={props.onRemove}
        aria-label={`Remove ${props.label}`}
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: 0,
          color: 'inherit',
          borderRadius: '50%',
          width: 16,
          height: 16,
          fontSize: 11,
          lineHeight: 1,
          cursor: 'pointer',
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ×
      </button>
    </span>
  );
}

function UnmatchedChip(props: {
  label: string;
  type: 'account' | 'contact';
  disabled?: boolean;
  onCreate: () => void;
  onDismiss: () => void;
}) {
  return (
    <span
      class="v2-pill"
      style={{
        height: 24,
        padding: '0 6px 0 10px',
        gap: 6,
        background: props.disabled ? 'rgba(255,255,255,0.03)' : 'var(--warn-tint)',
        color: props.disabled ? 'var(--text-faint)' : 'var(--warn)',
        border: '1px dashed',
        borderColor: props.disabled ? 'var(--border)' : 'rgba(251, 191, 36, 0.4)',
      }}
      title={
        props.disabled
          ? 'Account slot is already filled — remove the current one to swap'
          : `Create ${props.label} as a new ${props.type}`
      }
    >
      <button
        type="button"
        onClick={props.onCreate}
        disabled={props.disabled}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          padding: 0,
          font: 'inherit',
          cursor: props.disabled ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Icons.Plus size={10} />
        {props.label}
        <span
          class="mono"
          style={{ color: 'var(--text-faint)', fontSize: 10, marginLeft: 2 }}
        >
          {props.type}
        </span>
      </button>
      <button
        type="button"
        onClick={props.onDismiss}
        aria-label={`Dismiss ${props.label}`}
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: 0,
          color: 'inherit',
          borderRadius: '50%',
          width: 16,
          height: 16,
          fontSize: 11,
          lineHeight: 1,
          cursor: 'pointer',
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ×
      </button>
    </span>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Browser SpeechRecognition typing
// ───────────────────────────────────────────────────────────────────────────

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionErrorEvent) => void;
  onend: () => void;
  start(): void;
  stop(): void;
}
interface SpeechRecognitionEvent {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResult>;
}
interface SpeechRecognitionResult {
  isFinal: boolean;
  0: { transcript: string; confidence: number };
}
interface SpeechRecognitionErrorEvent {
  error: string;
}
interface SpeechRecognitionCtor {
  new (): SpeechRecognitionLike;
}

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1)}…`;
}

function relativeTimeShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const m = Math.round(ms / 60000);
  if (m < 1) return 'a moment ago';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
